import { useEffect, useState } from 'react'
import {
  Sparkles,
  Loader2,
  Globe,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Youtube,
  Music2,
  ExternalLink,
  Pencil,
} from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { toast } from '../../lib/shadcn/sonner'
import { useEnrichContact, useUpdateEnrichment } from '../../hooks/backend/crm'
import { formatDateTime } from '../../utils/format'
import { HelpText } from '../../components/HelpText'

export interface Enrichment {
  website: string | null
  facebook_url: string | null
  instagram_url: string | null
  twitter_url: string | null
  linkedin_url: string | null
  tiktok_url: string | null
  youtube_url: string | null
  enrichment_summary: string | null
  enriched_at: string | null
  enriched_by: string | null
}

interface Props {
  kind: 'lead' | 'customer'
  id: string | number
  data: Enrichment
  onEnriched: () => void
}

type UrlField =
  | 'website'
  | 'facebook_url'
  | 'instagram_url'
  | 'twitter_url'
  | 'linkedin_url'
  | 'tiktok_url'
  | 'youtube_url'

const LINKS: Array<{
  key: UrlField
  label: string
  placeholder: string
  icon: React.ComponentType<{ className?: string }>
}> = [
  { key: 'website', label: 'Website', placeholder: 'example.com', icon: Globe },
  { key: 'facebook_url', label: 'Facebook', placeholder: 'facebook.com/…', icon: Facebook },
  { key: 'instagram_url', label: 'Instagram', placeholder: 'instagram.com/…', icon: Instagram },
  { key: 'twitter_url', label: 'Twitter / X', placeholder: 'twitter.com/…', icon: Twitter },
  { key: 'linkedin_url', label: 'LinkedIn', placeholder: 'linkedin.com/company/…', icon: Linkedin },
  { key: 'tiktok_url', label: 'TikTok', placeholder: 'tiktok.com/@…', icon: Music2 },
  { key: 'youtube_url', label: 'YouTube', placeholder: 'youtube.com/@…', icon: Youtube },
]

type EditForm = Record<UrlField, string> & { enrichment_summary: string }

function toForm(data: Enrichment): EditForm {
  return {
    website: data.website ?? '',
    facebook_url: data.facebook_url ?? '',
    instagram_url: data.instagram_url ?? '',
    twitter_url: data.twitter_url ?? '',
    linkedin_url: data.linkedin_url ?? '',
    tiktok_url: data.tiktok_url ?? '',
    youtube_url: data.youtube_url ?? '',
    enrichment_summary: data.enrichment_summary ?? '',
  }
}

export default function EnrichmentCard({ kind, id, data, onEnriched }: Props) {
  const enrichContact = useEnrichContact()
  const updateEnrichment = useUpdateEnrichment()
  const [running, setRunning] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<EditForm>(() => toForm(data))

  useEffect(() => {
    if (editOpen) setForm(toForm(data))
  }, [editOpen, data])

  const handleEnrich = async () => {
    setRunning(true)
    try {
      await enrichContact.trigger({ kind, id })
      toast.success('Enrichment complete')
      onEnriched()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to enrich')
    } finally {
      setRunning(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await updateEnrichment.trigger({ kind, id, ...form })
      toast.success('Web presence saved')
      setEditOpen(false)
      onEnriched()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const foundLinks = LINKS.filter(l => {
    const v = data[l.key]
    return typeof v === 'string' && v.length > 0
  })
  const hasEnrichment = Boolean(data.enriched_at)

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-2 gap-2">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-primary" /> Web presence
        </h3>
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2"
            onClick={() => setEditOpen(true)}
          >
            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
          <Button
            variant={hasEnrichment ? 'outline' : 'default'}
            size="sm"
            className="h-7 px-2"
            onClick={handleEnrich}
            disabled={running}
          >
            {running ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5 mr-1" />
            )}
            {hasEnrichment ? 'Re-enrich' : 'Enrich with AI'}
          </Button>
        </div>
      </div>

      {data.enrichment_summary && (
        <p className="text-sm text-muted-foreground mb-3 whitespace-pre-wrap">{data.enrichment_summary}</p>
      )}

      {foundLinks.length > 0 ? (
        <div className="space-y-1.5">
          {foundLinks.map(({ key, label, icon: Icon }) => {
            const url = data[key] as string
            return (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 text-sm text-foreground hover:text-primary"
              >
                <Icon className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0" />
                <span className="min-w-[72px] text-muted-foreground">{label}</span>
                <span className="truncate flex-1 text-primary underline-offset-2 group-hover:underline">
                  {url.replace(/^https?:\/\//, '')}
                </span>
                <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
              </a>
            )
          })}
        </div>
      ) : hasEnrichment ? (
        <p className="text-sm text-muted-foreground">
          No web presence found. Click Edit to add links manually, or Re-enrich to try again.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground"><HelpText>
          Use AI to look up this {kind}&apos;s website, social profiles, and a short summary — or click Edit to enter them yourself.
        </HelpText></p>
      )}

      {data.enriched_at && (
        <p className="text-xs text-muted-foreground mt-3 pt-2 border-t border-border">
          Updated {formatDateTime(data.enriched_at)}
          {data.enriched_by ? ` by ${data.enriched_by}` : ''}
        </p>
      )}

      <Dialog open={editOpen} onOpenChange={o => { if (!saving) setEditOpen(o) }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit web presence</DialogTitle>
            <DialogDescription>
              Add or correct the website and social profile links. Leave a field blank to clear it.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto pr-1">
            {LINKS.map(({ key, label, placeholder, icon: Icon }) => (
              <div key={key} className="space-y-1.5">
                <Label htmlFor={`enrich-${key}`} className="flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5 text-muted-foreground" /> {label}
                </Label>
                <Input
                  id={`enrich-${key}`}
                  value={form[key]}
                  placeholder={placeholder}
                  onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label htmlFor="enrich-summary">Summary</Label>
              <Textarea
                id="enrich-summary"
                rows={3}
                value={form.enrichment_summary}
                placeholder="Short description of the business"
                onChange={e => setForm(p => ({ ...p, enrichment_summary: e.target.value }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
